import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

const SITE_URL = "https://pawground.app";

/**
 * Unified breadcrumb component: renders the visual breadcrumb trail
 * AND the matching JSON-LD BreadcrumbList schema so Google can show
 * the breadcrumb trail in search results.
 *
 * Pass `items` as [{ name, to }] — the last item is rendered as
 * non-clickable current-page text. Always include "Home" as the first
 * item; callers don't need to add it themselves.
 *
 * Skip JSON-LD by passing schemaOnly=false (visual only) or
 * visualOnly=true (schema only) — defaults emit both.
 */
const Breadcrumbs = ({ items = [], schemaOnly = false, visualOnly = false }) => {
  if (!items.length) return null;
  const trail = [{ name: "Home", to: "/" }, ...items];

  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      // Last item omits item URL per schema.org best practice.
      ...(i < trail.length - 1 && it.to
        ? { item: `${SITE_URL}${it.to}` }
        : {}),
    })),
  };

  return (
    <>
      {!visualOnly && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
          data-jsonld-id="breadcrumbs"
        />
      )}
      {!schemaOnly && (
        <nav
          aria-label="Breadcrumb"
          data-testid="breadcrumbs"
          className="flex items-center text-xs sm:text-sm text-brand-ink/60 mb-3 flex-wrap"
        >
          {trail.map((it, i) => {
            const isLast = i === trail.length - 1;
            return (
              <span key={`${it.name}-${i}`} className="inline-flex items-center">
                {i > 0 && (
                  <ChevronRight
                    className="w-3.5 h-3.5 mx-1.5 text-brand-ink/40"
                    aria-hidden
                  />
                )}
                {isLast || !it.to ? (
                  <span
                    aria-current={isLast ? "page" : undefined}
                    className="text-brand-ink/80 font-semibold truncate max-w-[20ch] sm:max-w-none"
                  >
                    {it.name}
                  </span>
                ) : (
                  <Link
                    to={it.to}
                    className="text-brand-forest hover:text-brand-forest-dark hover:underline truncate max-w-[20ch] sm:max-w-none"
                  >
                    {it.name}
                  </Link>
                )}
              </span>
            );
          })}
        </nav>
      )}
    </>
  );
};

export default Breadcrumbs;
